import { useStationStore } from '@/state/useStationStore';
import { useMemo } from 'react';

interface Bar {
  readonly label: string;
  readonly effectiveW: number;
  readonly requestedW: number;
}

export function PowerDashboard(): JSX.Element {
  const connectors = useStationStore((s) => s.connectors);
  const cpMaxPowerW = useStationStore((s) => s.cpMaxPowerW);

  const { bars, totalEffectiveW } = useMemo(() => {
    const out: Bar[] = [];
    let total = 0;
    for (const c of connectors.values()) {
      out.push({
        label: `Connector ${c.id}`,
        effectiveW: c.effectivePowerW,
        requestedW: c.requestedPowerW,
      });
      total += c.effectivePowerW;
    }
    return { bars: out, totalEffectiveW: total };
  }, [connectors]);

  const maxScale = Math.max(cpMaxPowerW, ...bars.map((b) => b.requestedW), 1);

  return (
    <section
      className="rounded border border-slate-700 bg-slate-900/60 p-4"
      aria-label="Power dashboard"
    >
      <header className="mb-3 flex items-baseline justify-between text-sm">
        <h2 className="font-medium text-slate-100">Power dashboard</h2>
        <span className="text-xs text-slate-400" data-testid="power-total">
          {(totalEffectiveW / 1000).toFixed(2)} / {(cpMaxPowerW / 1000).toFixed(0)} kW
        </span>
      </header>
      <svg viewBox={`0 0 320 ${bars.length * 26 + 8}`} className="w-full" aria-hidden="true">
        {bars.map((b, i) => {
          const y = i * 26 + 6;
          const widthReq = (b.requestedW / maxScale) * 280;
          const widthEff = (b.effectiveW / maxScale) * 280;
          return (
            <g key={b.label}>
              <text x={4} y={y + 12} className="fill-slate-300" fontSize="10">
                {b.label}
              </text>
              <rect x={90} y={y} width={280} height={16} className="fill-slate-800" />
              <rect x={90} y={y} width={widthReq} height={16} className="fill-slate-600" />
              <rect x={90} y={y} width={widthEff} height={16} className="fill-primary-base" />
              <text x={92 + widthEff + 4} y={y + 12} className="fill-slate-300" fontSize="9">
                {(b.effectiveW / 1000).toFixed(1)} kW
              </text>
            </g>
          );
        })}
      </svg>
    </section>
  );
}
